import { narrowEnum, nullableString } from '@/api/mappers/_shared';
import type {
  TaskDto,
  TaskPageDto,
  TaskPriorityCode,
  TaskStatusCode,
  TaskSummaryDto,
  TaskTypeCode,
  UpdateTaskInput,
} from '@/features/patient-record/api/tasks';
import type {
  PatientTaskCreateInput,
  PatientTaskPriority,
  PatientTaskStatus,
  PatientTaskSummaryViewModel,
  PatientTaskType,
  PatientTaskUpdateInput,
  PatientTaskViewModel,
  TaskInboxItemViewModel,
} from '@/features/patient-record/types';

/**
 * Task DTO ↔ view-model mappers.
 *
 * Wire format is snake_case / lower_snake_case, view model is camelCase /
 * SCREAMING_SNAKE_CASE. Translation lives here so the React layer never
 * has to think about which casing is current.
 */

const WIRE_TO_VM_TYPE: Record<TaskTypeCode, PatientTaskType> = {
  general: 'GENERAL',
  contact_patient: 'CONTACT_PATIENT',
  schedule_appointment: 'SCHEDULE_APPOINTMENT',
  order_test: 'ORDER_TEST',
  review_results: 'REVIEW_RESULTS',
  schedule_followup: 'SCHEDULE_FOLLOWUP',
  send_results_letter: 'SEND_RESULTS_LETTER',
};
const VM_TO_WIRE_TYPE: Record<PatientTaskType, TaskTypeCode> = {
  GENERAL: 'general',
  CONTACT_PATIENT: 'contact_patient',
  SCHEDULE_APPOINTMENT: 'schedule_appointment',
  ORDER_TEST: 'order_test',
  REVIEW_RESULTS: 'review_results',
  SCHEDULE_FOLLOWUP: 'schedule_followup',
  SEND_RESULTS_LETTER: 'send_results_letter',
};

const WIRE_TO_VM_PRIORITY: Record<TaskPriorityCode, PatientTaskPriority> = {
  low: 'LOW',
  medium: 'MEDIUM',
  high: 'HIGH',
  urgent: 'URGENT',
};
const VM_TO_WIRE_PRIORITY: Record<PatientTaskPriority, TaskPriorityCode> = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  URGENT: 'urgent',
};

const WIRE_TO_VM_STATUS: Record<TaskStatusCode, PatientTaskStatus> = {
  todo: 'TODO',
  done: 'DONE',
  cancelled: 'CANCELLED',
};
const VM_TO_WIRE_STATUS: Record<PatientTaskStatus, TaskStatusCode> = {
  TODO: 'todo',
  DONE: 'done',
  CANCELLED: 'cancelled',
};

const KNOWN_VM_TYPE: ReadonlySet<PatientTaskType> = new Set(
  Object.keys(VM_TO_WIRE_TYPE) as PatientTaskType[],
);
const KNOWN_VM_PRIORITY: ReadonlySet<PatientTaskPriority> = new Set(
  Object.keys(VM_TO_WIRE_PRIORITY) as PatientTaskPriority[],
);
const KNOWN_VM_STATUS: ReadonlySet<PatientTaskStatus> = new Set(
  Object.keys(VM_TO_WIRE_STATUS) as PatientTaskStatus[],
);

function vmType(code: TaskTypeCode | undefined): PatientTaskType {
  if (code !== undefined && code in WIRE_TO_VM_TYPE) return WIRE_TO_VM_TYPE[code];
  return 'GENERAL';
}
function vmPriority(code: TaskPriorityCode | undefined): PatientTaskPriority {
  if (code !== undefined && code in WIRE_TO_VM_PRIORITY) return WIRE_TO_VM_PRIORITY[code];
  return 'MEDIUM';
}
function vmStatus(code: TaskStatusCode | undefined): PatientTaskStatus {
  if (code !== undefined && code in WIRE_TO_VM_STATUS) return WIRE_TO_VM_STATUS[code];
  return 'TODO';
}

/** Maps the summary shape returned by `GET /persons/{guid}/tasks`. */
export function mapTaskSummary(
  dto: TaskSummaryDto,
  patientGuid: string,
): PatientTaskSummaryViewModel | null {
  if (typeof dto.id !== 'string' || dto.id.length === 0) return null;
  return {
    id: dto.id,
    patientGuid,
    type: vmType(dto.type),
    title: typeof dto.title === 'string' ? dto.title : '',
    assigneeId: nullableString(dto.assignee_id),
    priority: vmPriority(dto.priority),
    status: vmStatus(dto.status),
    dueDate: nullableString(dto.due_date),
  };
}

/**
 * Maps a cross-entity row from `GET /persons/tasks` (PTV2-1236). Unlike
 * {@link mapTaskSummary} the owning record isn't known ahead of time, so the
 * row keeps its own `entity_guid` / `entity_type` for the inbox to link back.
 */
export function mapTaskInboxItem(dto: TaskSummaryDto): TaskInboxItemViewModel | null {
  if (typeof dto.id !== 'string' || dto.id.length === 0) return null;
  // The OpenAPI schema names these camelCase; the live wire has historically
  // been snake_case (see TaskSummaryDto). Read camelCase first, fall back to
  // snake so the record links resolve regardless of the runtime naming.
  const entityType = dto.entityType ?? dto.entity_type;
  return {
    id: dto.id,
    entityGuid: nullableString(dto.entityGuid ?? dto.entity_guid),
    entityType: entityType === 'family' ? 'family' : 'person',
    type: vmType(dto.type),
    title: typeof dto.title === 'string' ? dto.title : '',
    assigneeId: nullableString(dto.assigneeId ?? dto.assignee_id),
    priority: vmPriority(dto.priority),
    status: vmStatus(dto.status),
    dueDate: nullableString(dto.dueDate ?? dto.due_date),
  };
}

export function mapTaskInboxItems(
  page: TaskPageDto | undefined,
): readonly TaskInboxItemViewModel[] {
  const content = page?.content ?? [];
  const out: TaskInboxItemViewModel[] = [];
  for (const row of content) {
    const mapped = mapTaskInboxItem(row);
    if (mapped !== null) out.push(mapped);
  }
  return out;
}

export function mapTaskSummaries(
  page: TaskPageDto | undefined,
  patientGuid: string,
): readonly PatientTaskSummaryViewModel[] {
  const content = page?.content ?? [];
  const out: PatientTaskSummaryViewModel[] = [];
  for (const row of content) {
    const mapped = mapTaskSummary(row, patientGuid);
    if (mapped !== null) out.push(mapped);
  }
  return out;
}

/** Maps the full `TaskDTO` (single-task detail + create/update responses). */
export function mapTask(dto: TaskDto, fallbackPatientGuid: string): PatientTaskViewModel | null {
  if (typeof dto.id !== 'string' || dto.id.length === 0) return null;
  const summaryDto: TaskSummaryDto = { id: dto.id };
  if (dto.entity_guid !== undefined) Object.assign(summaryDto, { entity_guid: dto.entity_guid });
  if (dto.entity_type !== undefined) Object.assign(summaryDto, { entity_type: dto.entity_type });
  if (dto.title !== undefined) Object.assign(summaryDto, { title: dto.title });
  if (dto.type !== undefined) Object.assign(summaryDto, { type: dto.type });
  if (dto.assignee_id !== undefined) Object.assign(summaryDto, { assignee_id: dto.assignee_id });
  if (dto.priority !== undefined) Object.assign(summaryDto, { priority: dto.priority });
  if (dto.status !== undefined) Object.assign(summaryDto, { status: dto.status });
  if (dto.due_date !== undefined) Object.assign(summaryDto, { due_date: dto.due_date });
  const summary = mapTaskSummary(
    summaryDto,
    nullableString(dto.entity_guid) ?? fallbackPatientGuid,
  );
  if (summary === null) return null;
  return {
    ...summary,
    description: nullableString(dto.description),
    notes: nullableString(dto.notes),
    completedAt: nullableString(dto.completed_at),
    completedBy: nullableString(dto.completed_by),
    cancelledAt: nullableString(dto.cancelled_at),
    cancelledBy: nullableString(dto.cancelled_by),
  };
}

/** VM create input → wire body (`TaskDTO`). Server-owned fields stay absent. */
export function createInputToDto(input: PatientTaskCreateInput): TaskDto {
  return {
    title: input.title,
    type: VM_TO_WIRE_TYPE[input.type],
    assignee_id: input.assigneeId,
    priority: VM_TO_WIRE_PRIORITY[input.priority],
    due_date: input.dueDate,
    description: input.description,
    notes: input.notes,
    status: 'todo',
  };
}

/** VM update input → JSON-Patch input (`UpdateTaskInput` in the api slice). */
export function updateInputToWire(input: PatientTaskUpdateInput): UpdateTaskInput {
  return {
    ...(input.title !== undefined ? { title: input.title } : {}),
    ...(input.type !== undefined ? { type: VM_TO_WIRE_TYPE[input.type] } : {}),
    ...(input.assigneeId !== undefined ? { assigneeId: input.assigneeId } : {}),
    ...(input.priority !== undefined ? { priority: VM_TO_WIRE_PRIORITY[input.priority] } : {}),
    ...(input.dueDate !== undefined ? { dueDate: input.dueDate } : {}),
    ...(input.description !== undefined ? { description: input.description } : {}),
    ...(input.notes !== undefined ? { notes: input.notes } : {}),
    ...(input.status !== undefined ? { status: VM_TO_WIRE_STATUS[input.status] } : {}),
  };
}

/** Narrowing helpers re-exported for tests / direct callers. */
export const KNOWN_TASK_TYPE = KNOWN_VM_TYPE;
export const KNOWN_TASK_PRIORITY = KNOWN_VM_PRIORITY;
export const KNOWN_TASK_STATUS = KNOWN_VM_STATUS;
function asStringOrNull(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}
export const narrowTaskType = (raw: unknown): PatientTaskType =>
  narrowEnum<PatientTaskType>(asStringOrNull(raw), KNOWN_VM_TYPE, 'GENERAL');
export const narrowTaskPriority = (raw: unknown): PatientTaskPriority =>
  narrowEnum<PatientTaskPriority>(asStringOrNull(raw), KNOWN_VM_PRIORITY, 'MEDIUM');
export const narrowTaskStatus = (raw: unknown): PatientTaskStatus =>
  narrowEnum<PatientTaskStatus>(asStringOrNull(raw), KNOWN_VM_STATUS, 'TODO');
